import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { AuditService } from './audit.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'preferred-language';
  private readonly DEFAULT_LANGUAGE = 'en';
  private readonly SUPPORTED_LANGUAGES = ['en', 'es'];

  /** Resolves when the current (or most recent) translation file load finishes. */
  private activeLanguageLoad$: Observable<void> = of(void 0);

  constructor(private translate: TranslateService, private audit: AuditService) {}

  /**
   * Initialize language on app startup. Returns an observable that completes
   * once the selected translation file has been loaded.
   */
  initializeLanguage(): Observable<void> {
    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE);

    const savedLanguage = this.getSavedLanguage();
    const browserLanguage = this.detectBrowserLanguage();
    const languageToUse = savedLanguage || browserLanguage || this.DEFAULT_LANGUAGE;

    this.activeLanguageLoad$ = this.loadLanguage(languageToUse).pipe(shareReplay(1));
    return this.activeLanguageLoad$;
  }

  /**
   * Wait until the active translation file load completes.
   * Use before navigating to translated views after a language change.
   */
  waitForTranslations(): Observable<void> {
    return this.activeLanguageLoad$;
  }

  /**
   * Get saved language from localStorage
   */
  private getSavedLanguage(): string | null {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved && this.SUPPORTED_LANGUAGES.includes(saved)) {
        return saved;
      }
    } catch (error) {
      // Silent fail
    }
    return null;
  }

  /**
   * Detect browser language
   */
  private detectBrowserLanguage(): string | null {
    try {
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (browserLang) {
        const langCode = browserLang.split('-')[0].toLowerCase();
        if (this.SUPPORTED_LANGUAGES.includes(langCode)) {
          return langCode;
        }
      }
    } catch (error) {
      // Silent fail
    }
    return null;
  }

  /**
   * Change language and persist to localStorage
   */
  useLanguage(language: string, trackAudit = true): void {
    const from = this.translate.currentLang || this.DEFAULT_LANGUAGE;
    const targetLanguage = this.SUPPORTED_LANGUAGES.includes(language)
      ? language
      : this.DEFAULT_LANGUAGE;

    if (targetLanguage !== from) {
      // Persist immediately so refresh keeps the user's choice even while es.json loads.
      this.saveLanguage(targetLanguage);
    }

    this.activeLanguageLoad$ = this.loadLanguage(targetLanguage).pipe(
      tap(() => {
        if (trackAudit && targetLanguage !== from) {
          this.audit.trackAction(
            "ACTION_LANGUAGE_CHANGE",
            "language_change",
            {
              input: {
                from,
                to: targetLanguage,
                fromName: this.getLanguageDisplayName(from),
                toName: this.getLanguageDisplayName(targetLanguage),
              },
              result: { success: true },
            },
            true
          );
        }
      }),
      shareReplay(1)
    );

    // Trigger the load — without a subscriber, translate.use() never runs.
    this.activeLanguageLoad$.subscribe();
  }

  private loadLanguage(language: string): Observable<void> {
    return this.translate.use(language).pipe(
      tap(() => {
        this.saveLanguage(language);
      }),
      map(() => void 0),
      catchError(() => {
        if (language !== this.DEFAULT_LANGUAGE) {
          // Fall back for display only; do not overwrite saved user preference.
          return this.translate.use(this.DEFAULT_LANGUAGE).pipe(
            map(() => void 0),
            catchError(() => of(void 0))
          );
        }
        return of(void 0);
      })
    );
  }

  /**
   * Save language preference to localStorage
   */
  private saveLanguage(language: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, language);
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this.translate.currentLang || this.DEFAULT_LANGUAGE;
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): string[] {
    return [...this.SUPPORTED_LANGUAGES];
  }

  /**
   * Get language display name
   */
  getLanguageDisplayName(languageCode: string): string {
    const names: { [key: string]: string } = {
      'en': 'English',
      'es': 'Español'
    };
    return names[languageCode] || languageCode;
  }
}
